import { FormEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-business-days-sk";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { useAdminDraft } from "@contexts/admin/AdminDraftsProvider";
import { adminRoutePaths } from "@pages/AdminDashboard/adminRoutes";
import { CaseReference as SharedCaseReference } from "@pages/AdminDashboard/CaseReference";
import { formatAdminDate } from "@pages/AdminDashboard/formatAdminDate";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminFontFamily, adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { phoneSearchFormLayout } from "@pages/AdminDashboard/theme/phoneSearchForm";
import { Banner } from "@pages/AdminDashboard/ui/Banner";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { Card } from "@pages/AdminDashboard/ui/Card";
import { ConfirmDialog } from "@pages/AdminDashboard/ui/ConfirmDialog";
import { ContextStrip } from "@pages/AdminDashboard/ui/ContextStrip";
import { EmptyStateCard } from "@pages/AdminDashboard/ui/DataTable";
import { ShortIdentifier } from "@pages/AdminDashboard/ui/DataTable/cells";
import { PageHeader } from "@pages/AdminDashboard/ui/PageHeader";
import { RadioCardGroup } from "@pages/AdminDashboard/ui/RadioCardGroup";
import { StatusBadge } from "@pages/AdminDashboard/ui/StatusBadge";
import { TextField } from "@pages/AdminDashboard/ui/TextField";
import { PatientIdentity } from "@pages/AdminDashboard/UserEmailLink";
import { findShipCase, loadShipAddresses, updateShipMethod } from "@store/admin/adminAction";
import {
  ShipAddressOutcome,
  ShipAddressState,
  ShipCase,
  ShipScreenState,
} from "@types-runway/admin";
import { Order, ShippingMethods, Trip } from "@types-runway/case";
import { RootState } from "@types-runway/state";

import { nameCase } from "./caseName";
import { ShipAddressDialog } from "./ShipAddressDialog";
import { describeShipAddress } from "./shipAddressFormat";

const { bannerTone, color, radius, shadow, typeScale } = adminTokens;
const [, spaceSm, spaceMd, spaceLg, spaceXl] = adminTokens.spacing;
const welcomeTitleSize = "16px";
const caseIdFieldWidth = 220;
const barPaddingStart = 16;
const barPaddingEnd = 4;
const shippedStatuses = ["out_for_delivery", "completed"];
const notYetSentLabel = "Not yet sent";
const defaultMethod = ShippingMethods.STANDARD;

const Page = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${spaceXl}px;
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const FindBar = styled.form<{ $isPhone: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spaceSm}px;
  padding: ${barPaddingEnd}px ${barPaddingEnd}px ${barPaddingEnd}px ${barPaddingStart}px;
  background-color: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.pill}px;
  ${({ $isPhone }) => $isPhone && phoneSearchFormLayout}
`;

const CaseIdSlot = styled.div`
  width: ${caseIdFieldWidth}px;
`;

const Welcome = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${barPaddingEnd}px;
  padding: ${spaceXl}px 0;
  text-align: center;
  color: ${color.inkSoft};
`;

const WelcomeTitle = styled.span`
  color: ${color.navy};
  font-size: ${welcomeTitleSize};
`;

// On a phone the shipping method stands above the shipping address (SHIPUI-R39).
const Cards = styled.div<{ $isPhone: boolean }>`
  gap: ${spaceXl}px;

  ${({ $isPhone }) =>
    $isPhone
      ? "display: flex; flex-direction: column;"
      : "display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: start;"}
`;

const CurrentMethod = styled.span`
  color: ${color.inkSoft};
  font-size: ${typeScale.meta.fontSize};

  strong {
    color: ${color.navy};
    font-family: ${typeScale.labelValue.fontFamily};
    font-weight: normal;
  }
`;

const Help = styled.p`
  margin: 0;
  color: ${color.inkSoft};
  font-size: ${typeScale.meta.fontSize};
  line-height: ${typeScale.meta.lineHeight};
`;

// On a phone a card's Button spans the card (SHIPUI-R40).
const CardActions = styled.div<{ $isPhone: boolean }>`
  display: flex;
  justify-content: flex-end;

  ${({ $isPhone }) => $isPhone && "& > button { width: 100%; }"}
`;

const AddressBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: ${spaceMd + 2}px ${spaceLg}px;
  background-color: ${color.canvas};
  border-radius: ${radius.row}px;
  box-shadow: ${shadow.card};
`;

const AddressLabel = styled.span`
  color: ${color.inkSoft};
  font-size: 12px;
`;

const AddressLine = styled.span`
  font-family: ${typeScale.labelValue.fontFamily};
  font-size: 15px;
`;

const AddressPlace = styled.span`
  color: ${color.inkSoft};
`;

const ActiveOrder = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spaceSm}px;
`;

// The design sets the Order's ID in Medium navy (SHIPUI-R35).
const OrderId = styled.strong`
  color: ${color.navy};
  font-family: ${adminFontFamily.medium};
  font-weight: normal;
`;

const methods = Object.values(ShippingMethods);

const millisecondsPerDay = 24 * 60 * 60 * 1000;
// A departure in more than this many days is normal; closer ones warn (SHIPUI-R34).
const warningWithinDays = 10;

// Calendar days from today to the calendar date of a departure, negative once it has passed, or
// null when the value is not a date (SHIPUI-R33, R36).
const daysUntil = (departure: string): number | null => {
  const date = moment.utc(departure);
  if (!date.isValid()) return null;

  const today = new Date();

  return Math.round(
    (Date.UTC(date.year(), date.month(), date.date()) -
      Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
      millisecondsPerDay
  );
};

const countDays = (days: number) => `${days} ${days === 1 ? "day" : "days"}`;

const describeDaysLeft = (days: number) => {
  if (days === 0) return "(today)";
  if (days < 0) return `(departed ${countDays(-days)} ago)`;

  return `(in ${countDays(days)})`;
};

const DaysLeft = styled.span<{ $days: number }>`
  color: ${({ $days }) => {
    if ($days < 1) return bannerTone.error.text;

    return $days <= warningWithinDays ? bannerTone.warning.text : color.inkSoft;
  }};
`;

const describeTrip = (trip?: Trip & { destination?: string }) => {
  if (!trip?.from) return "No trip date";

  const days = daysUntil(trip.from);
  const departs = `departs ${formatAdminDate(trip.from)}`;
  if (days === null) return trip.destination ? `${trip.destination} · ${departs}` : departs;

  return (
    <>
      {trip.destination ? `${trip.destination} · ${departs} ` : `${departs} `}
      <DaysLeft $days={days}>{describeDaysLeft(days)}</DaysLeft>
    </>
  );
};

const CaseReference = ({ shipCase }: { shipCase: ShipCase }) => (
  <SharedCaseReference caseNumber={shipCase.id} caseId={shipCase.caseId} />
);

interface MethodBannerProps {
  shipCase: ShipCase;
  currentMethod: string;
  update: ShipScreenState["update"];
  onSeeOrders: () => void;
}

const MethodBanner = ({ shipCase, currentMethod, update, onSeeOrders }: MethodBannerProps) => {
  const seeOrders = { label: "See Orders in Redo", onAction: onSeeOrders };

  if (update.status === "replaced") {
    return (
      <Banner
        tone="success"
        title={`Shipping method is now ${update.method}.`}
        body={`OID ${update.predecessorOrderId} was replaced by OID ${update.successorOrderId}, which carries the new method and was sent to the pharmacy. Paid by the original invoice; the User was not charged.`}
        action={seeOrders}
      />
    );
  }
  if (update.status === "caseOnly") {
    return (
      <Banner
        tone="success"
        title={
          <>
            <CaseReference shipCase={shipCase} /> now ships {update.method}.
          </>
        }
        body="This Case has no Order yet, so only the Case was updated. The Order will use this method when it is created."
      />
    );
  }
  if (update.status === "failed") {
    return (
      <Banner
        tone="error"
        title="The shipping method wasn't updated."
        body={`Request failed (${update.reason}). The Case and its Order still ship ${currentMethod}. Your selection is kept so you can try again.`}
      />
    );
  }
  if (update.status === "replacedNotSent") {
    return (
      <Banner
        tone="error"
        title="The Order was replaced, but the new method wasn't sent."
        body={`OID ${update.predecessorOrderId} was replaced by OID ${update.successorOrderId}. Setting its method or sending it to the pharmacy failed. Open it in Redo to finish.`}
        action={seeOrders}
      />
    );
  }

  return null;
};

interface AddressBannerProps {
  shipCase: ShipCase;
  outcome: ShipAddressOutcome;
  onOpenRedo: () => void;
}

const AddressBanner = ({ shipCase, outcome, onOpenRedo }: AddressBannerProps) => {
  if (outcome.status === "none") return null;

  const where = describeShipAddress(outcome.address);

  if (outcome.status === "shipped") {
    return (
      <Banner
        tone="warning"
        title="Address saved. The Order has already shipped."
        body={
          <>
            <CaseReference shipCase={shipCase} /> now uses {where}, but OID {outcome.orderId} is out for
            delivery and can't be re-sent. Replace it to ship a corrective Order to the new address
            under the same payment.
          </>
        }
        action={{ label: "Replace Order (reship)", onAction: onOpenRedo }}
      />
    );
  }
  if (outcome.status === "notResent") {
    return (
      <Banner
        tone="error"
        title="Address saved, but the Order wasn't re-sent."
        body={
          <>
            <CaseReference shipCase={shipCase} /> now ships to {where}, but sending OID{" "}
            {outcome.orderId} to the pharmacy failed. Open it in Redo to finish.
          </>
        }
        action={{ label: "See Orders in Redo", onAction: onOpenRedo }}
      />
    );
  }

  const body = outcome.orderId
    ? `The address was saved to the Case first, then OID ${outcome.orderId} was re-sent to the pharmacy with it.`
    : "This Case has no Order yet, so only the Case was updated.";

  return (
    <Banner
      tone="success"
      title={
        <>
          <CaseReference shipCase={shipCase} /> now ships to {where}.
        </>
      }
      body={body}
    />
  );
};

// The Ship screen: finds a Case by its Case ID and fixes how its active Order ships. This part
// changes the shipping method (SHIPUI-R1…R22) and the shipping address (SHIPUI-R23…R31).
const ShipScreen = () => {
  const isPhone = useIsPhoneLayout();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { lookup, shipCase, orders, update } = useSelector<RootState, ShipScreenState>(
    (state) => state.admin.shipScreen
  );
  const shipAddress = useSelector<RootState, ShipAddressState>((state) => state.admin.shipAddress);
  const entry = new URLSearchParams(location.search).get("case") ?? "";

  // What the admin entered and searched stays while other screens of the admin are open (SHIPUI-R38).
  const [caseIdField, setCaseIdField] = useAdminDraft("ship.caseIdField", entry);
  const [searchedCaseId, setSearchedCaseId] = useAdminDraft("ship.searchedCaseId", entry);
  const [chosenMethod, setChosenMethod] = useState<string>(defaultMethod);
  const [isConfirming, setIsConfirming] = useState(false);

  const currentMethod = shipCase?.shipping_method ?? defaultMethod;
  const activeOrder: Order | undefined = orders.find((order) => order.status !== "cancelled");
  const hasShipped = Boolean(activeOrder && shippedStatuses.includes(activeOrder.status));
  const isPending = update.status === "pending";

  const findCase = (caseId: string) => {
    setSearchedCaseId(caseId);
    dispatch(findShipCase({ caseId }));
  };

  // An entry in the page address (a link from the Order or Case Search) is searched at once; a
  // new link to the same screen carries a new location key, so it searches again (SHIPUI-R4).
  useEffect(() => {
    if (!entry) return;

    setCaseIdField(entry);
    findCase(entry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, location.key]);

  useEffect(() => {
    setChosenMethod(currentMethod);
  }, [currentMethod, shipCase?.id]);

  // The confirmation closes when the change it started ends, not whenever an earlier outcome
  // is on screen, so a retry after a failure opens it again (SHIPUI-R15).
  const wasPending = useRef(false);
  useEffect(() => {
    if (wasPending.current && !isPending) setIsConfirming(false);
    wasPending.current = isPending;
  }, [isPending]);

  const submitFind = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const caseId = caseIdField.trim();
    if (!caseId || lookup === "pending") return;

    findCase(caseId);
  };

  const sendMethodChange = () =>
    shipCase &&
    dispatch(
      updateShipMethod({
        caseNumber: shipCase.id as number,
        shippingMethod: chosenMethod,
        activeOrder: activeOrder ? { id: activeOrder.id, order_id: activeOrder.order_id } : null,
      })
    );

  const requestMethodChange = () => {
    if (activeOrder) {
      setIsConfirming(true);
      return;
    }

    sendMethodChange();
  };

  const keepCurrentMethod = () => {
    setIsConfirming(false);
    setChosenMethod(currentMethod);
  };

  const openRedo = () =>
    history.push(`${adminRoutePaths.redo}?case=${encodeURIComponent(shipCase?.caseId ?? "")}`);

  const openAddressDialog = () =>
    dispatch(loadShipAddresses({ userId: shipCase?.UserId as string }));

  const isUpdateUnavailable =
    chosenMethod === currentMethod ||
    hasShipped ||
    isPending ||
    update.status === "replacedNotSent";

  const methodHelp = (() => {
    if (hasShipped) return "The Order has shipped. Replace it in Redo to ship it again.";
    if (activeOrder) {
      return "Updating replaces the active Order so the pharmacy receives the new method. The User is not charged again.";
    }

    return "No Order exists yet, so only the Case is updated.";
  })();

  const address = shipCase?.shippingAddress;

  return (
    <Page>
      <PageHeader
        title="Ship"
        subtitle="Fix how a Case's Order ships: method and address."
        aside={
          <FindBar $isPhone={isPhone} role="search" aria-label="Find a Case" onSubmit={submitFind}>
            <CaseIdSlot>
              <TextField
                label="Case ID"
                isLabelHidden
                value={caseIdField}
                onChange={(event) => setCaseIdField(event.target.value)}
              />
            </CaseIdSlot>
            <Button
              look="primary"
              type="submit"
              disabled={lookup === "pending" || !caseIdField.trim()}
              isPending={lookup === "pending"}
              pendingLabel="Find Case"
            >
              Find Case
            </Button>
          </FindBar>
        }
      />

      {lookup === "idle" && (
        <Welcome>
          <WelcomeTitle>Find a Case by its Case ID</WelcomeTitle>
          <span>Its Order&apos;s shipping method and address open here.</span>
        </Welcome>
      )}

      {lookup === "not_found" && (
        <div data-testid="ship-case-not-found" role="alert" aria-live="assertive">
          <EmptyStateCard
            title="Case not found"
            message={`No Case has the Case ID ${searchedCaseId}. Check the spelling.`}
          />
        </div>
      )}

      {lookup === "error" && (
        <Banner
          tone="error"
          title="The Case lookup failed."
          body="Nothing changed. Try again."
          action={{
            label: "Try again",
            onAction: () => findCase(caseIdField.trim() || searchedCaseId),
          }}
        />
      )}

      {lookup === "found" && shipCase && (
        <>
          <MethodBanner
            shipCase={shipCase}
            currentMethod={currentMethod}
            update={update}
            onSeeOrders={openRedo}
          />
          <AddressBanner shipCase={shipCase} outcome={shipAddress.outcome} onOpenRedo={openRedo} />
          <ContextStrip
            label="Case context"
            identifiers={[
              <CaseReference key="case" shipCase={shipCase} />,
              ...(shipCase.User ? [<PatientIdentity key="patient" patient={shipCase.User} />] : []),
              describeTrip(shipCase.trip),
              <ActiveOrder key="order">
                Active Order:{" "}
                {activeOrder ? (
                  <>
                    <OrderId>
                      <ShortIdentifier value={activeOrder.order_id} />
                    </OrderId>
                    <StatusBadge status={activeOrder.status ?? notYetSentLabel} />
                  </>
                ) : (
                  <OrderId>None yet</OrderId>
                )}
              </ActiveOrder>,
            ]}
          />
          <Cards $isPhone={isPhone}>
            <Card
              title="Shipping method"
              aside={
                <CurrentMethod>
                  Current: <strong>{currentMethod}</strong>
                </CurrentMethod>
              }
            >
              <RadioCardGroup
                legend="Shipping method"
                isLegendHidden
                name="ship-method"
                options={methods.map((method) => ({
                  value: method,
                  label: method,
                  tag: method === currentMethod ? "Current" : undefined,
                }))}
                value={chosenMethod}
                onChange={setChosenMethod}
              />
              <Help>{methodHelp}</Help>
              <CardActions $isPhone={isPhone}>
                <Button look="primary" disabled={isUpdateUnavailable} onClick={requestMethodChange}>
                  Update method
                </Button>
              </CardActions>
            </Card>
            <div data-testid="ship-address-card">
              <Card title="Shipping address">
                <AddressBox>
                  <AddressLabel>Ships to</AddressLabel>
                  <AddressLine>
                    {address
                      ? `${address.street}${address.secondary ? `, ${address.secondary}` : ""}`
                      : "—"}
                  </AddressLine>
                  {address && (
                    <AddressPlace>{`${address.city}, ${address.state} ${address.postalCode}`}</AddressPlace>
                  )}
                </AddressBox>
                <Help>
                  {hasShipped
                    ? "The Order has shipped: the new address applies to a corrective Order."
                    : "Not shipped yet: changing the address re-sends the Order to the pharmacy with it."}
                </Help>
                <CardActions $isPhone={isPhone}>
                  <Button look="secondary" onClick={openAddressDialog}>
                    Change address
                  </Button>
                </CardActions>
              </Card>
            </div>
          </Cards>
          <ShipAddressDialog
            shipCase={shipCase}
            activeOrder={activeOrder}
            hasShipped={hasShipped}
          />
          {activeOrder && (
            <ConfirmDialog
              isOpen={isConfirming}
              eyebrow="Change shipping method"
              title={`Ship ${nameCase(shipCase)} by ${chosenMethod}?`}
              consequences={[
                `${nameCase(shipCase)} is set to ${chosenMethod}.`,
                `OID ${activeOrder.order_id} is replaced: cancelled at the pharmacy and kept as history. A new Order carries the new method, paid by the original invoice.`,
                "The new Order is sent to the pharmacy.",
              ]}
              note={`The pharmacy doesn't reliably apply method changes to an existing Order, which is why the Order is replaced. The User keeps ${nameCase(shipCase)} and gets a new tracking number.`}
              dismissLabel={`Keep ${currentMethod}`}
              confirmLabel="Update and replace"
              pendingLabel="Updating…"
              isPending={isPending}
              onDismiss={keepCurrentMethod}
              onConfirm={sendMethodChange}
            />
          )}
        </>
      )}
    </Page>
  );
};

export default ShipScreen;
